// Operations Dashboard KPI values — null-preserving, so "unknown is never 0".
// A real 0 stays 0 (and animates to $0); an absent source is null and renders "—".
// Pure + exported so both cases are unit-tested per tile (kpiValues.test.js).
// Serves events (t27) and landscaping (t6), not only pest.
import { num } from "../../components/Analytics/metricValue";

// null-aware arithmetic: if any operand is unknown, the result is unknown.
const mul = (a, b) => (a === null ? null : a * b);
const addKnown = (a, b) => (a === null || b === null ? null : a + b);

/**
 * @param summary  /analytics/revenue-summary payload (or null if that call failed)
 * @param metrics  /dashboard/metrics payload (or null)
 * @param collectedThisMonth  this month's collected figure (number or null) — the
 *        caller resolves it from the month series; null when there is no month data.
 * @returns {object} one value per KPI: a finite number (incl. real 0) or null (unknown).
 */
export function computeKpiValues(summary, metrics, collectedThisMonth) {
  const mrr = num(summary?.mrr);
  const churned = num(summary?.churned_mrr);
  const paused = num(summary?.paused_mrr);
  const activeCust = num(metrics?.customers_active);
  const inactiveCust = num(metrics?.inactive_customer_count);
  // book = active + churned + paused MRR; unknown if any part is unknown.
  const book = addKnown(addKnown(mrr, churned), paused);
  // churn % = churned share of book. Real 0 when book is a real 0; unknown when
  // either is unknown; a positive book divides.
  let churnPct;
  if (churned === null || book === null) churnPct = null;
  else if (book === 0) churnPct = 0;
  else churnPct = (churned / book) * 100;

  return {
    active_mrr: mrr,
    arr: mul(mrr, 12),
    collected: num(collectedThisMonth),
    active_customers: activeCust,
    total_customers: addKnown(activeCust, inactiveCust),
    churned_customers: inactiveCust,
    churn_pct: churnPct,
    paused_count: num(summary?.inactive_agreements),
    churned_mrr: churned,
    paused_mrr: paused,
  };
}

/** Format one KPI value for display. null/undefined → "—"; a real number formats. */
export function formatKpi(value, { pctFmt = false, plain = false } = {}) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  if (pctFmt) return `${n.toFixed(1)}%`;
  if (plain) return Math.round(n).toLocaleString();
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}
